import { useEffect, useState } from "react";
import {
    Box,
    Button,
    Paper,
    Typography,
    TextField,
    Grid,
    Stack,
    Autocomplete,
    FormGroup,
    FormControlLabel,
    Checkbox,
} from "@mui/material";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { createStation } from "../../api/station";
import { Close, Save } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { Landmark } from "../../types/api";
import { listLandmarks } from "../../api/landmark";

const amenityOptions = [
    { key: "info", label: "จุดประชาสัมพันธ์" },
    { key: "buddha", label : "พระพุทธรูป"},
    { key: "market", label: "ตลาดนัด" },
    { key: "souvenir", label: "ร้านจำหน่ายของที่ระลึก" },
    { key: "toilet", label: "ห้องน้ำ" },
    { key: "ticket", label: "อาคารจำหน่ายบัตร" },
    { key: "firstAid", label: "จุดปฐมพยาบาล" },
    { key: "shop", label: "ร้านค้า" },
    { key: "bicycle", label: "จุดบริการจักรยาน" },
    { key: "coffee", label: "ร้านกาแฟ" },
    { key: "restaurant", label: "ร้านอาหารโครงการหลวง" },
    { key: "hotel", label: "โรงแรม" },
];



delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
    iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
    shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

function FlyToLocation({ position }: { position: [number, number] }) {
    const map = useMap();
    map.flyTo(position, map.getZoom());
    return null;
}

export default function StationCreatePage() {
    const navigate = useNavigate()

    const [nameTh, setNameTh] = useState("");
    const [nameEn, setNameEn] = useState("");
    const [nameZh, setNameZh] = useState("");
    const [latInput, setLatInput] = useState("18.74485267563325");
    const [lngInput, setLngInput] = useState("98.92795715183036");
    const [markerPos, setMarkerPos] = useState<[number, number]>([
        18.74485267563325,
        98.92795715183036,
    ]);
    const [flyTarget, setFlyTarget] = useState<[number, number] | null>(null);
    const [landmarkOptions, setLandmarkOptions] = useState<Landmark[]>([]);
    const [selectedLandmarks, setSelectedLandmarks] = useState<Landmark[]>([]);
    const [amenities, setAmenities] = useState<Record<string, boolean>>({});

    const updateMarker = () => {
        const lat = parseFloat(latInput);
        const lng = parseFloat(lngInput);
        if (!isNaN(lat) && !isNaN(lng)) {
            const pos: [number, number] = [lat, lng];
            setMarkerPos(pos);
            setFlyTarget(pos);
        } else {
            alert("กรุณากรอก Latitude และ Longitude ให้ถูกต้อง");
        }
    };

    const toggleAmenity = (key: string) => {
        setAmenities((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const handleSubmit = async () => {
        try {
            const newStation = {
                nameTh,
                nameEn,
                nameZh,
                latitude: parseFloat(latInput),
                longitude: parseFloat(lngInput),
                isDisabled: false,
                amenities,
                landmarkIds: selectedLandmarks.map((lm) => lm.id),
            };
            await createStation(newStation);
            alert("บันทึกสถานีเรียบร้อย ✅");
            navigate("/stations");
        } catch (err: any) {
            alert("บันทึกล้มเหลว: " + err.message);
        }
    };

    function MapClickHandler({ setMarker }: { setMarker: (pos: [number, number]) => void }) {
        useMapEvents({
            click(e) {
                const pos: [number, number] = [e.latlng.lat, e.latlng.lng];
                setMarker(pos);
            },
        });
        return null;
    }

    useEffect(() => {
        listLandmarks()
            .then(setLandmarkOptions)
            .catch((err) => alert("โหลด landmarks ไม่สำเร็จ: " + err.message));
    }, []);

    

    return (
        <Box>
            <Typography variant="h6" mb={2}>
                เพิ่มสถานีใหม่
            </Typography>
            <Paper sx={{ p: 3 }}>
                <Stack gap={3}>
                    <Grid container spacing={2}>
                        <Grid size={4}>
                            <TextField fullWidth label="ชื่อ (ไทย)" value={nameTh} onChange={(e) => setNameTh(e.target.value)} />
                        </Grid>
                        <Grid size={4}>
                            <TextField fullWidth label="ชื่อ (อังกฤษ)" value={nameEn} onChange={(e) => setNameEn(e.target.value)} />
                        </Grid>
                        <Grid size={4}>
                            <TextField fullWidth label="ชื่อ (จีน)" value={nameZh} onChange={(e) => setNameZh(e.target.value)} />
                        </Grid>

                        <Grid size={12}>
                            <Autocomplete
                                multiple
                                options={landmarkOptions}
                                getOptionLabel={(option) => option.nameTh}
                                value={selectedLandmarks}
                                onChange={(_, newValue) => setSelectedLandmarks(newValue)}
                                renderInput={(params) => (
                                    <TextField {...params} label="เลือก Landmarks" placeholder="ค้นหา..." />
                                )}
                            />
                        </Grid>

                        <Grid size={6}>
                            <TextField
                                fullWidth
                                label="Latitude"
                                type="number"
                                value={latInput}
                                onChange={(e) => setLatInput(e.target.value)}
                            />
                        </Grid>
                        <Grid size={6}>
                            <TextField
                                fullWidth
                                label="Longitude"
                                type="number"
                                value={lngInput}
                                onChange={(e) => setLngInput(e.target.value)}
                            />
                        </Grid>
                    </Grid>

                    <Typography variant="subtitle1">สิ่งอำนวยความสะดวก</Typography>
                    <FormGroup>
                        <Grid container spacing={1}>
                            {amenityOptions.map((item) => (
                                <Grid size={{ xs: 6, md: 4 }} key={item.key}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={!!amenities[item.key]}
                                                onChange={() => toggleAmenity(item.key)}
                                            />
                                        }
                                        label={item.label}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </FormGroup>

                    <Button variant="outlined" color="warning" onClick={updateMarker}>
                        ปักหมุดตำแหน่ง
                    </Button>

                    <Box sx={{ height: 400 }}>
                        <MapContainer
                            center={markerPos}
                            zoom={16}
                            style={{ height: "100%", width: "100%" }}
                        >
                            <TileLayer
                                attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            />
                            <Marker position={markerPos} />
                            {flyTarget && <FlyToLocation position={flyTarget} />}
                            <MapClickHandler setMarker={(pos) => {
                                setMarkerPos(pos);
                                setLatInput(pos[0].toString());
                                setLngInput(pos[1].toString());
                            }} />
                        </MapContainer>
                    </Box>

                    <Box display="flex" justifyContent="flex-end" gap={2}>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<Save />}
                            onClick={handleSubmit}
                        >
                            บันทึก
                        </Button>

                    </Box>
                </Stack>
            </Paper>
        </Box>
    );
}